/**
 * Logistics module strings (`logistics.*`), merged into the dictionary through `extra.ts`.
 * `logisticsEn` is typed against `logisticsPl`, so a missing key is a compile error.
 */
import { logisticsUxEn, logisticsUxPl } from './logistics-ux';

type Shape<T> = { [K in keyof T]: T[K] extends string ? string : Shape<T[K]> };

export const logisticsPl = {
  /** Redesigned views (cards, run states, chart headlines) — kept in `logistics-ux.ts`. */
  ux: logisticsUxPl,
  subtitle: 'Dostawy do klientów, kursy i problemy — co jedzie, co dojechało i co wymaga reakcji.',
  loadError: 'Nie udało się wczytać danych logistycznych',
  views: 'Widok logistyki',
  view: { today: 'Dziś', deliveries: 'Dostawy', routes: 'Trasy', problems: 'Problemy' },
  nav: { prev: 'Poprzedni dzień', next: 'Następny dzień', today: 'Dziś', yesterday: 'Wczoraj', tomorrow: 'Jutro', pickDay: 'Wybierz dzień' },
  status: { PLANNED: 'Zaplanowana', IN_TRANSIT: 'W drodze', DELIVERED: 'Dostarczona', FAILED: 'Nieudana', CANCELLED: 'Anulowana' },
  tab: { all: 'Wszystkie', PLANNED: 'Zaplanowane', IN_TRANSIT: 'W drodze', DELIVERED: 'Dostarczone', FAILED: 'Nieudane', CANCELLED: 'Anulowane' },
  failure: {
    NO_ONE_AT_SITE: 'Nikogo na miejscu',
    REFUSED: 'Odmowa przyjęcia',
    WRONG_ADDRESS: 'Błędny adres',
    DAMAGED_GOODS: 'Uszkodzony towar',
    NO_PAYMENT: 'Brak płatności',
    VEHICLE_FAILURE: 'Awaria pojazdu',
    TIME_EXCEEDED: 'Przekroczony czas pracy',
    OTHER: 'Inny powód',
  },
  flag: {
    late: 'Spóźniona',
    lateDays: 'Spóźniona o {days} d',
    moved: 'Przesunięta: {count}×',
    fromErp: 'Z ERP',
    fromErpHint: 'Status przyjęty z ERP: WZ wydana, dzień dostawy minął — bez potwierdzenia kierowcy.',
  },
  stats: {
    total: 'Dostawy',
    totalHint: 'w wybranym zakresie, anulowane: {cancelled}',
    planned: 'Zaplanowane',
    plannedHint: 'czekają na załadunek',
    inTransit: 'W drodze',
    inTransitHint: 'jadą do klientów',
    delivered: 'Dostarczone',
    deliveredHint: 'zamknięte: {done} z {total}',
    noneHint: 'brak dostaw',
    failed: 'Nieudane',
    failedHint: 'wymagają decyzji',
    late: 'Spóźnione',
    lateHint: 'po planowanym dniu',
    onTime: 'Terminowość 30 dni',
    onTimeHint: 'dostarczonych w okresie: {delivered}',
    onTimeNone: 'brak dostarczonych dostaw',
  },
  insight: {
    failed: 'Nieudane dostawy: {count} — warto ustalić powód i przesunąć albo ponowić.',
    late: 'Spóźnione dostawy: {count}.',
    open: 'Otwarte problemy z ostatnich 30 dni: {count}.',
    clear: 'Wszystko jedzie zgodnie z planem — brak nieudanych i spóźnionych dostaw.',
    show: 'Pokaż problemy',
  },
  today: {
    runs: 'Kursy dnia',
    runsCount: 'Kursy: {count}',
    empty: 'Brak kursów w tym dniu',
    emptyToday: 'Na dziś nie zaplanowano żadnych dostaw. Zajrzyj do listy dostaw, by zobaczyć kolejne dni.',
    emptyDay: 'W wybranym dniu nie było ani nie zaplanowano dostaw.',
  },
  run: {
    deliveries: 'Dostaw: {count}',
    late: 'Spóźnione: {count}',
    progress: 'Zrealizowano {pct}%',
    planned: 'Zaplanowane',
    inTransit: 'W drodze',
    delivered: 'Dostarczone',
    failed: 'Nieudane',
    noDriver: 'Bez kierowcy',
    noVehicle: 'Bez pojazdu',
    vehicle: 'Pojazd',
    stops: 'Przystanki kursu',
    noStops: 'Klienci tego kursu nie mają współrzędnych — nie ma ich na mapie.',
    stopsNote: 'Lista pokazuje przystanki, które mają współrzędne (te z mapy).',
    allDeliveries: 'Wszystkie dostawy kursu',
    routeDetail: 'Szczegóły trasy',
    profitability: 'Rentowność trasy',
  },
  map: { fit: 'Pokaż wszystkie', noCoords: 'Brak przystanków ze współrzędnymi' },
  trend: {
    title: 'Trend 30 dni',
    subtitle: 'Dostawy dzień po dniu, {from} – {to}',
    empty: 'Brak dostaw w ostatnich 30 dniach.',
    aria: 'Dostawy dzień po dniu od {from} do {to}: w terminie, spóźnione i nieudane',
    total: 'Dostaw: {count}',
    onTime: 'w terminie: {count}',
    late: 'spóźnione: {count}',
    failed: 'nieudane: {count}',
    onTimeRate: 'Terminowość: {pct}',
    lateRate: 'Spóźnione: {pct}',
    failedRate: 'Nieudane: {pct}',
    legendOnTime: 'W terminie',
    legendLate: 'Spóźnione',
    legendFailed: 'Nieudane',
  },
  worst: {
    title: 'Najgorsze trasy',
    subtitle: 'Najniższa terminowość w 30 dniach (od 20 dostaw)',
    empty: 'Żadna trasa nie ma jeszcze dość dostaw, by ją porównać.',
    deliveries: 'dostaw: {count}',
  },
  filters: {
    searchPlaceholder: 'Szukaj: klient, miasto, dokument, pojazd…',
    period: 'Okres',
    from: 'Od dnia',
    to: 'Do dnia',
    sort: 'Sortowanie',
    route: 'Trasa',
    allRoutes: 'Wszystkie trasy',
    rep: 'Handlowiec',
    allReps: 'Wszyscy handlowcy',
    customer: 'Klient…',
    problems: 'Tylko problemy',
    problemsHint: 'Nieudane albo spóźnione dostawy',
  },
  period: { today: 'Dziś', yesterday: 'Wczoraj', d7: '7 dni', d30: '30 dni', d90: '90 dni', next7: 'Najbliższe 7 dni', all: 'Cały okres', custom: 'Własny zakres' },
  sort: { '-plannedAt': 'Najnowsze najpierw', plannedAt: 'Najstarsze najpierw', customer: 'Klient A–Z', route: 'Trasa' },
  list: {
    plan: 'plan',
    empty: 'Brak dostaw',
    emptyBody: 'Zmień filtry lub zakres dat — nic nie pasuje do bieżącego wyboru.',
    select: 'Zaznacz dostawę: {name}',
  },
  problems: {
    title: 'Problemy z dostawami',
    subtitle: 'Nieudane i spóźnione dostawy — od najstarszych, z szybkimi akcjami.',
    emptyTitle: 'Brak problemów',
    emptyBody: 'W tym okresie nie ma nieudanych ani spóźnionych dostaw.',
    tab: { all: 'Wszystkie problemy', FAILED: 'Nieudane', PLANNED: 'Zaległe — zaplanowane', IN_TRANSIT: 'Zaległe — w drodze', DELIVERED: 'Dostarczone z opóźnieniem' },
  },
  action: {
    DELIVERED: 'Oznacz jako dostarczoną',
    FAILED: 'Oznacz jako nieudaną',
    IN_TRANSIT: 'Oznacz jako w drodze',
    PLANNED: 'Cofnij do zaplanowanej',
    CANCELLED: 'Anuluj dostawę',
    move: 'Przesuń',
    deliveredShort: 'Dostarczona',
    moveShort: 'Przesuń',
  },
  bulk: {
    selected: 'Zaznaczono: {count}',
    selectPage: 'Zaznacz stronę',
    deselectPage: 'Odznacz wszystkie',
    delivered: 'Oznacz jako dostarczone',
    failed: 'Oznacz jako nieudane',
    inTransit: 'Oznacz jako w drodze',
    reason: { NOT_FOUND: 'poza zakresem', TRANSITION_NOT_ALLOWED: 'zmiana niedozwolona', FAILURE_REASON_REQUIRED: 'brak powodu' },
  },
  statusDialog: {
    title: { PLANNED: 'Cofnij do zaplanowanej', IN_TRANSIT: 'Dostawa w drodze', DELIVERED: 'Dostawa dostarczona', FAILED: 'Dostawa nieudana', CANCELLED: 'Anuluj dostawę' },
    confirm: { PLANNED: 'Cofnij', IN_TRANSIT: 'Oznacz jako w drodze', DELIVERED: 'Oznacz jako dostarczoną', FAILED: 'Oznacz jako nieudaną', CANCELLED: 'Anuluj dostawę' },
    forOne: 'Klient: {name}',
    forMany: 'Zaznaczone dostawy: {count}',
    reason: 'Powód niepowodzenia',
    reasonPlaceholder: 'Wybierz powód',
    reasonRequired: 'Wybierz powód nieudanej dostawy.',
    deliveredAt: 'Kiedy dostarczono',
    time: 'Godzina dostarczenia',
    deliveredAtHint: 'Zostaw puste, jeśli dostarczono przed chwilą. Nie można podać przyszłości.',
    future: 'Moment dostarczenia nie może być w przyszłości.',
    note: 'Notatka',
    notePlaceholder: 'Opcjonalnie — co warto wiedzieć o tej zmianie',
  },
  reschedule: {
    title: 'Przesuń dostawę',
    confirm: 'Przesuń',
    day: 'Nowy dzień',
    route: 'Trasa',
    notePlaceholder: 'Opcjonalnie — powód przesunięcia',
    past: 'Wybierz dzień nie wcześniejszy niż dziś.',
    nothing: 'Zmień dzień albo trasę.',
  },
  route: { none: 'Bez trasy' },
  toast: {
    status: 'Status zmieniony: {status}',
    bulk: 'Zmieniono status ({status}): {count}',
    skipped: 'Pominięto: {count} ({reasons})',
    moved: 'Dostawa przesunięta',
  },
  errors: { generic: 'Nie udało się zapisać zmiany. Spróbuj ponownie.', conflict: 'Ta zmiana nie jest dozwolona dla bieżącego stanu dostawy — odśwież listę.' },
  history: { status: 'Zmiana statusu', plan: 'Zmiana planu' },
  drawer: {
    title: 'Dostawa',
    loadError: 'Nie udało się wczytać dostawy',
    readOnly: 'Tylko odczyt',
    problem: 'Wymaga uwagi',
    problemFailed: 'Dostawa nieudana.',
    problemFailedReason: 'Dostawa nieudana: {reason}.',
    problemDeliveredLate: 'Dostarczona z opóźnieniem: {days} d po planie.',
    problemOverdue: 'Po terminie: {days} d po planowanym dniu, a dostawa nie jest zamknięta.',
    planned: 'Plan',
    deliveredAt: 'Dostarczona',
    route: 'Trasa',
    vehicle: 'Pojazd i kierowca',
    document: 'Dokument',
    owner: 'Handlowiec',
    customer: 'Klient',
    openCustomer: 'Otwórz klienta',
    notes: 'Notatki',
    noNotes: 'Brak notatek.',
    history: 'Historia zdarzeń',
    noHistory: 'Brak zmian — dostawa jest w stanie początkowym.',
  },
  routes: {
    empty: 'Brak tras',
    emptyBody: 'W wybranym okresie nie ma tras pasujących do filtra.',
    search: 'Szukaj trasy, kierowcy, pojazdu…',
    profitability: 'Rentowność tras',
    inactive: 'Nieaktywna',
    runDays: 'dni jazdy: {days}',
    col: { route: 'Trasa', crew: 'Kierowca / pojazd', deliveries: 'Dostawy', onTime: 'Terminowość', failed: 'Nieudane', late: 'Spóźnione', km: 'Km', cost: 'Koszt', costPer: 'Koszt / dostawa' },
    sort: { deliveries: 'Najwięcej dostaw', onTime: 'Najgorsza terminowość', failed: 'Najwięcej problemów', cost: 'Najdroższe', costPer: 'Najdroższa dostawa', name: 'Kod trasy' },
    stats: {
      routes: 'Trasy',
      routesHint: 'aktywnych: {active}',
      deliveries: 'Dostawy',
      km: 'Przejechane km',
      cost: 'Koszt transportu',
      perDelivery: '{value} na dostawę',
      runDays: 'dni jazdy: {days}',
    },
  },
  routeDetail: {
    title: 'Trasa',
    loadError: 'Nie udało się wczytać trasy',
    stops: 'Przystanki trasy',
    runDates: 'Dni jazdy:',
    noStops: 'Brak dostaw w tym dniu',
    noStopsBody: 'Wybierz inny dzień jazdy.',
    crew: 'Obsada',
    summaryLine: 'W okresie: {km}, koszt {cost}',
    costs: 'Koszty 30 dni',
    costsSubtitle: 'Paliwo, kierowca, pojazd i pozostałe — dzień po dniu',
    costsAria: 'Koszty trasy dzień po dniu',
    noCosts: 'Brak kosztów trasy w ostatnich 30 dniach.',
    total30: 'Razem 30 dni',
    perKm: 'Koszt na km',
    fuel: 'Paliwo',
    driver: 'Kierowca',
    vehicle: 'Pojazd',
    other: 'Inne',
    daysWithCosts: 'dni z kosztami',
  },
} as const;

export const logisticsEn: Shape<typeof logisticsPl> = {
  ux: logisticsUxEn,
  subtitle: 'Customer deliveries, runs and problems — what is on the road, what arrived and what needs a reaction.',
  loadError: 'Could not load the logistics data',
  views: 'Logistics view',
  view: { today: 'Today', deliveries: 'Deliveries', routes: 'Routes', problems: 'Problems' },
  nav: { prev: 'Previous day', next: 'Next day', today: 'Today', yesterday: 'Yesterday', tomorrow: 'Tomorrow', pickDay: 'Pick a day' },
  status: { PLANNED: 'Planned', IN_TRANSIT: 'In transit', DELIVERED: 'Delivered', FAILED: 'Failed', CANCELLED: 'Cancelled' },
  tab: { all: 'All', PLANNED: 'Planned', IN_TRANSIT: 'In transit', DELIVERED: 'Delivered', FAILED: 'Failed', CANCELLED: 'Cancelled' },
  failure: {
    NO_ONE_AT_SITE: 'Nobody on site',
    REFUSED: 'Refused',
    WRONG_ADDRESS: 'Wrong address',
    DAMAGED_GOODS: 'Damaged goods',
    NO_PAYMENT: 'No payment',
    VEHICLE_FAILURE: 'Vehicle breakdown',
    TIME_EXCEEDED: 'Working time exceeded',
    OTHER: 'Other reason',
  },
  flag: {
    late: 'Late',
    lateDays: 'Late by {days} d',
    moved: 'Moved: {count}×',
    fromErp: 'From ERP',
    fromErpHint: 'Status taken from the ERP: WZ issued, delivery day passed — not confirmed by the driver.',
  },
  stats: {
    total: 'Deliveries',
    totalHint: 'in the chosen range, cancelled: {cancelled}',
    planned: 'Planned',
    plannedHint: 'waiting to be loaded',
    inTransit: 'In transit',
    inTransitHint: 'on the way to customers',
    delivered: 'Delivered',
    deliveredHint: 'closed: {done} of {total}',
    noneHint: 'no deliveries',
    failed: 'Failed',
    failedHint: 'need a decision',
    late: 'Late',
    lateHint: 'after the planned day',
    onTime: 'On time, 30 days',
    onTimeHint: 'delivered in the period: {delivered}',
    onTimeNone: 'no delivered deliveries',
  },
  insight: {
    failed: 'Failed deliveries: {count} — find out why and move or repeat them.',
    late: 'Late deliveries: {count}.',
    open: 'Open problems from the last 30 days: {count}.',
    clear: 'Everything is on schedule — no failed or late deliveries.',
    show: 'Show problems',
  },
  today: {
    runs: 'Runs of the day',
    runsCount: 'Runs: {count}',
    empty: 'No runs on this day',
    emptyToday: 'No deliveries are planned for today. Check the deliveries list for the coming days.',
    emptyDay: 'There were no deliveries, and none are planned, on the chosen day.',
  },
  run: {
    deliveries: 'Deliveries: {count}',
    late: 'Late: {count}',
    progress: '{pct}% done',
    planned: 'Planned',
    inTransit: 'In transit',
    delivered: 'Delivered',
    failed: 'Failed',
    noDriver: 'No driver',
    noVehicle: 'No vehicle',
    vehicle: 'Vehicle',
    stops: 'Stops of the run',
    noStops: 'The customers of this run have no coordinates — they are not on the map.',
    stopsNote: 'The list shows the stops that have coordinates (the ones on the map).',
    allDeliveries: 'All deliveries of the run',
    routeDetail: 'Route details',
    profitability: 'Route profitability',
  },
  map: { fit: 'Show all', noCoords: 'No stops with coordinates' },
  trend: {
    title: '30-day trend',
    subtitle: 'Deliveries day by day, {from} – {to}',
    empty: 'No deliveries in the last 30 days.',
    aria: 'Deliveries day by day from {from} to {to}: on time, late and failed',
    total: 'Deliveries: {count}',
    onTime: 'on time: {count}',
    late: 'late: {count}',
    failed: 'failed: {count}',
    onTimeRate: 'On time: {pct}',
    lateRate: 'Late: {pct}',
    failedRate: 'Failed: {pct}',
    legendOnTime: 'On time',
    legendLate: 'Late',
    legendFailed: 'Failed',
  },
  worst: {
    title: 'Worst routes',
    subtitle: 'Lowest on-time rate in 30 days (from 20 deliveries)',
    empty: 'No route has enough deliveries to be compared yet.',
    deliveries: 'deliveries: {count}',
  },
  filters: {
    searchPlaceholder: 'Search: customer, city, document, vehicle…',
    period: 'Period',
    from: 'From day',
    to: 'To day',
    sort: 'Sort',
    route: 'Route',
    allRoutes: 'All routes',
    rep: 'Sales rep',
    allReps: 'All reps',
    customer: 'Customer…',
    problems: 'Problems only',
    problemsHint: 'Failed or late deliveries',
  },
  period: { today: 'Today', yesterday: 'Yesterday', d7: '7 days', d30: '30 days', d90: '90 days', next7: 'Next 7 days', all: 'All time', custom: 'Custom range' },
  sort: { '-plannedAt': 'Newest first', plannedAt: 'Oldest first', customer: 'Customer A–Z', route: 'Route' },
  list: {
    plan: 'planned',
    empty: 'No deliveries',
    emptyBody: 'Change the filters or the date range — nothing matches the current selection.',
    select: 'Select delivery: {name}',
  },
  problems: {
    title: 'Delivery problems',
    subtitle: 'Failed and late deliveries — oldest first, with quick actions.',
    emptyTitle: 'No problems',
    emptyBody: 'There are no failed or late deliveries in this period.',
    tab: { all: 'All problems', FAILED: 'Failed', PLANNED: 'Overdue — planned', IN_TRANSIT: 'Overdue — in transit', DELIVERED: 'Delivered late' },
  },
  action: {
    DELIVERED: 'Mark as delivered',
    FAILED: 'Mark as failed',
    IN_TRANSIT: 'Mark as in transit',
    PLANNED: 'Revert to planned',
    CANCELLED: 'Cancel delivery',
    move: 'Move',
    deliveredShort: 'Delivered',
    moveShort: 'Move',
  },
  bulk: {
    selected: 'Selected: {count}',
    selectPage: 'Select page',
    deselectPage: 'Deselect all',
    delivered: 'Mark as delivered',
    failed: 'Mark as failed',
    inTransit: 'Mark as in transit',
    reason: { NOT_FOUND: 'out of scope', TRANSITION_NOT_ALLOWED: 'change not allowed', FAILURE_REASON_REQUIRED: 'reason missing' },
  },
  statusDialog: {
    title: { PLANNED: 'Revert to planned', IN_TRANSIT: 'Delivery in transit', DELIVERED: 'Delivery delivered', FAILED: 'Delivery failed', CANCELLED: 'Cancel delivery' },
    confirm: { PLANNED: 'Revert', IN_TRANSIT: 'Mark as in transit', DELIVERED: 'Mark as delivered', FAILED: 'Mark as failed', CANCELLED: 'Cancel delivery' },
    forOne: 'Customer: {name}',
    forMany: 'Selected deliveries: {count}',
    reason: 'Failure reason',
    reasonPlaceholder: 'Choose a reason',
    reasonRequired: 'Choose the reason of the failed delivery.',
    deliveredAt: 'When it was delivered',
    time: 'Delivery time',
    deliveredAtHint: 'Leave empty if it was delivered just now. The future is not allowed.',
    future: 'The delivery moment cannot be in the future.',
    note: 'Note',
    notePlaceholder: 'Optional — anything worth knowing about this change',
  },
  reschedule: {
    title: 'Move delivery',
    confirm: 'Move',
    day: 'New day',
    route: 'Route',
    notePlaceholder: 'Optional — why it is moved',
    past: 'Choose a day no earlier than today.',
    nothing: 'Change the day or the route.',
  },
  route: { none: 'No route' },
  toast: {
    status: 'Status changed: {status}',
    bulk: 'Status changed ({status}): {count}',
    skipped: 'Skipped: {count} ({reasons})',
    moved: 'Delivery moved',
  },
  errors: { generic: 'Could not save the change. Please try again.', conflict: 'This change is not allowed in the delivery’s current state — refresh the list.' },
  history: { status: 'Status change', plan: 'Plan change' },
  drawer: {
    title: 'Delivery',
    loadError: 'Could not load the delivery',
    readOnly: 'Read only',
    problem: 'Needs attention',
    problemFailed: 'Delivery failed.',
    problemFailedReason: 'Delivery failed: {reason}.',
    problemDeliveredLate: 'Delivered late: {days} d after the plan.',
    problemOverdue: 'Overdue: {days} d after the planned day and the delivery is not closed.',
    planned: 'Plan',
    deliveredAt: 'Delivered',
    route: 'Route',
    vehicle: 'Vehicle and driver',
    document: 'Document',
    owner: 'Sales rep',
    customer: 'Customer',
    openCustomer: 'Open customer',
    notes: 'Notes',
    noNotes: 'No notes.',
    history: 'Event history',
    noHistory: 'No changes — the delivery is in its initial state.',
  },
  routes: {
    empty: 'No routes',
    emptyBody: 'No routes match the filter in the chosen period.',
    search: 'Search route, driver, vehicle…',
    profitability: 'Route profitability',
    inactive: 'Inactive',
    runDays: 'run days: {days}',
    col: { route: 'Route', crew: 'Driver / vehicle', deliveries: 'Deliveries', onTime: 'On time', failed: 'Failed', late: 'Late', km: 'Km', cost: 'Cost', costPer: 'Cost / delivery' },
    sort: { deliveries: 'Most deliveries', onTime: 'Worst on-time rate', failed: 'Most problems', cost: 'Most expensive', costPer: 'Costliest delivery', name: 'Route code' },
    stats: {
      routes: 'Routes',
      routesHint: 'active: {active}',
      deliveries: 'Deliveries',
      km: 'Distance driven',
      cost: 'Transport cost',
      perDelivery: '{value} per delivery',
      runDays: 'run days: {days}',
    },
  },
  routeDetail: {
    title: 'Route',
    loadError: 'Could not load the route',
    stops: 'Stops of the route',
    runDates: 'Run days:',
    noStops: 'No deliveries on this day',
    noStopsBody: 'Pick another run day.',
    crew: 'Crew',
    summaryLine: 'In the period: {km}, cost {cost}',
    costs: '30-day costs',
    costsSubtitle: 'Fuel, driver, vehicle and other — day by day',
    costsAria: 'Route costs day by day',
    noCosts: 'No route costs in the last 30 days.',
    total30: 'Total, 30 days',
    perKm: 'Cost per km',
    fuel: 'Fuel',
    driver: 'Driver',
    vehicle: 'Vehicle',
    other: 'Other',
    daysWithCosts: 'days with costs',
  },
};
